import { supabase } from './supabaseClient'
import { Recipe, RecipeFormData, RecipeSource } from '@/types/recipe' // Ensure paths are correct, added RecipeCategory

const RECIPES_TABLE = 'recipes' // Adjust if your table name is different
// const TAGS_TABLE = 'tags' // Removed as it's unused with current getAllTags logic

// Helper function to parse tags from database text field
const parseTags = (tagsString: string | null): string[] => {
  if (!tagsString) return [];
  try {
    // Try parsing as JSON array first
    const parsed = JSON.parse(tagsString);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    // If not JSON, treat as comma-separated string
    return tagsString.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);
  }
};

// Helper function to serialize tags for database
const serializeTags = (tags: string[]): string => {
  return JSON.stringify(tags);
};

// Default public image URL - never expires since bucket is public
const DEFAULT_IMAGE_URL = 'https://bzvzoqvyxfrgaxsokfva.supabase.co/storage/v1/object/public/recipeimage/chezalex.jpg';

// Helper function to upload image to Supabase storage
const uploadImageToSupabase = async (imageData: string, recipeId: string): Promise<string | null> => {
  try {
    // Convert base64 to blob
    const response = await fetch(imageData);
    const blob = await response.blob();
    
    // Generate unique filename
    const fileName = `recipe-${recipeId}-${Date.now()}.jpg`;
    
    // Upload to Supabase storage
    const { error } = await supabase.storage
      .from('recipeimage')
      .upload(fileName, blob, {
        contentType: 'image/jpeg',
        upsert: true
      });
    
    if (error) {
      return null;
    }
    
    // Get public URL - bucket is public so no signed URLs needed (they expire!)
    const { data: urlData } = supabase.storage
      .from('recipeimage')
      .getPublicUrl(fileName);
      
    return urlData.publicUrl;
  } catch {
    return null;
  }
};

// Helper function to normalize image URLs to public format
// Converts expired signed URLs to public URLs (bucket is public)
const normalizeImageUrl = (imageUrl: string): string => {
  if (!imageUrl) return DEFAULT_IMAGE_URL;
  
  // If it's a signed URL from our bucket, convert to public URL
  if (imageUrl.includes('/storage/v1/object/sign/recipeimage/')) {
    // Extract filename from signed URL (before the ?token=)
    const pathPart = imageUrl.split('/recipeimage/')[1];
    if (pathPart) {
      const fileName = pathPart.split('?')[0];
      if (fileName) {
        return `https://bzvzoqvyxfrgaxsokfva.supabase.co/storage/v1/object/public/recipeimage/${fileName}`;
      }
    }
  }
  
  // If it's already a public URL from our bucket, return as-is
  if (imageUrl.includes('/storage/v1/object/public/recipeimage/')) {
    return imageUrl;
  }
  
  // For other URLs (external images, etc.), return as-is
  return imageUrl;
};

export const addRecipe = async (recipeForm: RecipeFormData): Promise<Recipe> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("User not authenticated");

  // Explicit column allowlist — never spread the incoming form object.
  // Studio/assistant hand-offs attach UI-only flags (fromStudio, studioPersona,
  // autoGenerateImage, studioGeneratedImage, fromAssistant), and Recipe carries
  // computed fields (isOwner, creatorName, creatorCountry). None are columns on
  // public.recipes; PostgREST rejects unknown keys with 400 / PGRST204.
  const initialRecipeData = {
    title: recipeForm.title,
    description: recipeForm.description,
    category: recipeForm.category,
    tags: serializeTags(recipeForm.tags || []),
    ingredients: recipeForm.ingredients,
    method: recipeForm.method,
    notes: recipeForm.notes || '',
    // P1.3: structured enrichment layer (JSONB, nullable) — explicit null for
    // legacy recipes; markdown columns above stay canonical.
    structured: recipeForm.structured ?? null,
    image_url: DEFAULT_IMAGE_URL, // Public URL - never expires
    has_custom_image: false,
    isFavorite: recipeForm.isFavorite || false,
    share: recipeForm.share !== undefined ? recipeForm.share : true,
    user_id: user.id,
    dateCreated: new Date().toISOString(),
    dateModified: new Date().toISOString(),
    source: recipeForm.source || RecipeSource.User // Fallback for safety
  };

  const { data, error } = await supabase
    .from(RECIPES_TABLE)
    .insert(initialRecipeData)
    .select()
    .single();

  if (error) {
    throw new Error('Failed to save recipe. Please try again.');
  }

  // Now handle image upload with the real recipe ID if there's a base64 image
  if (recipeForm.image_url && recipeForm.image_url.startsWith('data:')) {
    try {
      const imageUrl = await uploadImageToSupabase(recipeForm.image_url, data.id.toString());
      
      if (imageUrl) {
        // Update the recipe with the uploaded image URL
        const { error: updateError } = await supabase
          .from(RECIPES_TABLE)
          .update({
            image_url: imageUrl,
            has_custom_image: true
          })
          .eq('id', data.id);

        if (updateError) {
          // Don't throw error - recipe is saved, just without custom image
        } else {
          // Update the data object to reflect the image update
          data.image_url = imageUrl;
          data.has_custom_image = true;
        }
      }
    } catch {
      // Don't throw error - recipe is saved, just without custom image
    }
  } else if (recipeForm.image_url && !recipeForm.image_url.startsWith('data:')) {
    // If image_url is provided but not base64, assume it's a direct URL
    const { error: updateError } = await supabase
      .from(RECIPES_TABLE)
      .update({
        image_url: recipeForm.image_url,
        has_custom_image: true
      })
      .eq('id', data.id);

    if (!updateError) {
      data.image_url = recipeForm.image_url;
      data.has_custom_image = true;
    }
  }

  // Return recipe with user context
  return await addUserContextToRecipe(data, user.id);
};

// Helper function to add user context to recipes efficiently
const addUserContextToRecipes = async (items: any[], userId?: string): Promise<Recipe[]> => {
  let userFavorites: Set<string> = new Set();
  let userProfiles: Map<string, { name?: string; country?: string }> = new Map();
  
  if (userId && items.length > 0) {
    // Get ALL user favorites in one query instead of per recipe
    try {
      const { data: favoriteData } = await supabase
        .from('user_favorites')
        .select('recipe_id')
        .eq('user_id', userId);
      
      if (favoriteData) {
        userFavorites = new Set(favoriteData.map(f => String(f.recipe_id)));
      }
    } catch {
      // Continue without favorites instead of breaking
    }
  }

  // Get unique user IDs from recipes to fetch their profiles
  const uniqueUserIds = [...new Set(items.map(item => item.user_id).filter(Boolean))];
  
  
  if (uniqueUserIds.length > 0) {
    try {
      const { data: profileData } = await supabase
        .from('user_profiles')
        .select('id, name, country')
        .in('id', uniqueUserIds);
      
      if (profileData) {
        userProfiles = new Map(
          profileData.map(profile => [
            profile.id,
            { name: profile.name, country: profile.country }
          ])
        );
      }
    } catch {
      // Continue without profile data instead of breaking
    }
  }

  // Use regular map since normalizeImageUrl is synchronous now
  const mappedRecipes = items.map((item) => {
    const creatorProfile = item.user_id ? userProfiles.get(item.user_id) : undefined;
    
    // Normalize image URL to public format (converts expired signed URLs)
    const imageUrl = normalizeImageUrl(item.image_url || DEFAULT_IMAGE_URL);
    
    const recipe = {
      id: item.id,
      title: item.title || '',
      description: item.description || '',
      category: item.category || undefined,
      tags: parseTags(item.tags),
      ingredients: item.ingredients || '',
      method: item.method || '',
      notes: item.notes || '',
      image_url: imageUrl,
      has_custom_image: item.has_custom_image || false,
      dateCreated: item.dateCreated,
      dateModified: item.dateModified,
      isFavorite: userFavorites.has(String(item.id)), // Check in Set
      share: item.share !== undefined ? item.share : true,
      user_id: item.user_id,
      isOwner: userId ? item.user_id === userId : false,
      source: item.source || RecipeSource.User, // Add source with fallback
      // Add creator profile information
      creatorName: creatorProfile?.name,
      creatorCountry: creatorProfile?.country
    } as Recipe;
    
    return recipe;
  });
  
  return mappedRecipes;
};

// Single recipe context helper
const addUserContextToRecipe = async (item: any, userId?: string): Promise<Recipe> => {
  const recipes = await addUserContextToRecipes([item], userId);
  return recipes[0];
};

export const getAllRecipes = async (): Promise<Recipe[]> => {
  const { data: { user } } = await supabase.auth.getUser();
  
  const { data, error } = await supabase
    .from(RECIPES_TABLE)
    .select('*')
    .order('dateCreated', { ascending: false });

  if (error) {
    throw error;
  }
  
  // Add user context efficiently in batch
  return await addUserContextToRecipes(data, user?.id);
};

export const getRecipesPaginated = async (limit: number = 25, offset: number = 0): Promise<Recipe[]> => {
  const { data: { user } } = await supabase.auth.getUser();
  
  const { data, error } = await supabase
    .from(RECIPES_TABLE)
    .select('*')
    .order('dateCreated', { ascending: false })
    .range(offset, offset + limit - 1);

  if (error) {
    throw error;
  }
  
  // Add user context efficiently in batch
  return await addUserContextToRecipes(data, user?.id);
};

export const getRecipesFiltered = async (
  limit: number = 25,
  offset: number = 0,
  searchQuery: string = '',
  category: string = 'all',
  showFavoritesOnly: boolean = false,
  showOwnRecipesOnly: boolean = false
): Promise<{ data: Recipe[], totalCount: number }> => {
  const { data: { user: currentUser } } = await supabase.auth.getUser();
  
  // Call the RPC function for filtered recipes
  const { data, error } = await supabase.rpc('get_recipes_filtered', {
    p_user_id: currentUser?.id || null,
    p_search_query: searchQuery || '',
    p_category: category || 'all',
    p_favorites_only: showFavoritesOnly,
    p_own_recipes_only: showOwnRecipesOnly,
    p_limit: limit,
    p_offset: offset
  });

  if (error) {
    throw error;
  }

  // Extract total count from first row (all rows have same total_count)
  const totalCount = data && data.length > 0 ? Number(data[0].total_count) : 0;
  
  // Map the data to Recipe format and add user context
  const recipes = data ? data.map((row: any) => ({
    id: row.id,
    title: row.title,
    description: row.description,
    category: row.category,
    tags: row.tags,
    ingredients: row.ingredients,
    method: row.method,
    notes: row.notes,
    isFavorite: row.isFavorite,
    dateCreated: row.dateCreated,
    dateModified: row.dateModified,
    user_id: row.user_id,
    image_url: row.image_url,
    has_custom_image: row.has_custom_image,
    share: row.share,
    source: row.source
  })) : [];
  
  // Add user context (isOwner, isFavorite from user_favorites, creatorName, etc.)
  const mappedData = await addUserContextToRecipes(recipes, currentUser?.id);
  
  return { data: mappedData, totalCount };
};

export const getRecipeById = async (id: string): Promise<Recipe | null> => {
  const { data: { user } } = await supabase.auth.getUser();
  
  const { data: item, error } = await supabase
    .from(RECIPES_TABLE)
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') { // PostgREST error code for "Not Found"
      return null;
    }
    throw error;
  }

  if (!item) {
    return null; // Should be covered by PGRST116, but as a fallback
  }

  return await addUserContextToRecipe(item, user?.id);
};

// User favorites functionality
export const toggleUserFavorite = async (recipeId: string): Promise<void> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("User not authenticated");

    // Check if already favorited
    const { data: existing, error: selectError } = await supabase
      .from('user_favorites')
      .select('id')
      .eq('user_id', user.id)
      .eq('recipe_id', parseInt(recipeId)) // Convert to integer
      .maybeSingle(); // Use maybeSingle instead of single to avoid errors

    if (selectError) {
      throw new Error('Failed to check favorite status');
    }

    if (existing) {
      // Remove favorite
      const { error } = await supabase
        .from('user_favorites')
        .delete()
        .eq('user_id', user.id)
        .eq('recipe_id', parseInt(recipeId));
      
      if (error) {
        throw new Error('Failed to remove favorite');
      }
    } else {
      // Add favorite
      const { error } = await supabase
        .from('user_favorites')
        .insert({ user_id: user.id, recipe_id: parseInt(recipeId) });
      
      if (error) {
        throw new Error('Failed to add favorite');
      }
    }
};

export const updateRecipe = async (id: string, updates: Partial<RecipeFormData>): Promise<Recipe> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("User not authenticated");

  // Check if user can edit this recipe (admin or owner)
  const { data: recipe } = await supabase
    .from(RECIPES_TABLE)
    .select('user_id')
    .eq('id', id)
    .single();

  const { data: profile } = await supabase
    .from('user_profiles')
    .select('role')
    .eq('id', user.id)
    .single();

  const isAdmin = profile?.role === 'admin';
  const isOwner = recipe?.user_id === user.id;

  if (!isAdmin && !isOwner) {
    throw new Error("Access denied: You can only edit your own recipes");
  }

  // `updates` is Partial<RecipeFormData>, which now correctly uses image_url
  const { image_url: formInputImageUrl, tags, ...otherUpdates } = updates;

  // Explicit column allowlist — `otherUpdates` can carry non-column keys from
  // RecipeFormData (isOwner, creatorName, creatorCountry, studioPersona) which
  // PostgREST rejects with 400/PGRST204. user_id is deliberately excluded so
  // ownership cannot be reassigned through a generic form update.
  const UPDATABLE_COLUMNS = [
    'title',
    'description',
    'category',
    'ingredients',
    'method',
    'notes',
    'isFavorite',
    'share',
    'source',
    'structured',
    'has_custom_image',
  ] as const;
  const finalUpdateData: { [key: string]: any } = {};
  for (const key of UPDATABLE_COLUMNS) {
    if (key in otherUpdates) {
      finalUpdateData[key] = (otherUpdates as Record<string, unknown>)[key];
    }
  }

  if (tags !== undefined) { // Check for undefined to allow clearing tags with an empty array
    finalUpdateData.tags = serializeTags(tags || []);
  }
  
  // Handle image update with proper cleanup
  if (formInputImageUrl && formInputImageUrl.startsWith('data:')) { // New base64 image uploaded
    // Get current recipe to check for existing custom image
    const { data: currentRecipe } = await supabase
      .from(RECIPES_TABLE)
      .select('image_url, has_custom_image')
      .eq('id', id)
      .single();
    
    // Delete old custom image if it exists and is not the default
    if (currentRecipe?.image_url && currentRecipe?.has_custom_image &&
        currentRecipe.image_url.includes('/storage/v1/object/public/recipeimage/') &&
        !currentRecipe.image_url.includes('chezalex.jpg')) {
      try {
        const oldFileName = currentRecipe.image_url.split('/').pop();
        if (oldFileName) {
          await supabase.storage.from('recipeimage').remove([oldFileName]);
        }
      } catch {
        // Continue with upload even if deletion fails
      }
    }
    
    const newImageUrl = await uploadImageToSupabase(formInputImageUrl, id);
    finalUpdateData.image_url = newImageUrl;
    finalUpdateData.has_custom_image = !!newImageUrl;
  } else if (formInputImageUrl !== undefined) { // Direct URL or clearing the image
    finalUpdateData.image_url = formInputImageUrl; // Can be null or empty string to clear
    finalUpdateData.has_custom_image = !!formInputImageUrl; // Set to false if cleared
  }
  // If formInputImageUrl is undefined, image_url and has_custom_image are not part of `updates` and won't be changed.

  // CRITICAL: Remove any assignment to a legacy 'image' field.
  // The line `recipeDataToUpdate.image = getCategoryImage(updates.category);` was removed.

  // Remove undefined fields to avoid overwriting with null, unless explicitly set to null
  Object.keys(finalUpdateData).forEach(key => {
    if (finalUpdateData[key] === undefined) {
      delete finalUpdateData[key];
    }
  });
  
  if (Object.keys(finalUpdateData).length > 0) { // Only update if there are changes
    finalUpdateData.dateModified = new Date().toISOString();
  } else {
    // If only image was "updated" to the same non-base64 URL, or no actual field changes
    // we might not need to call update. Or, if dateModified must always update:
    if (formInputImageUrl !== undefined && !formInputImageUrl.startsWith('data:')) {
         finalUpdateData.dateModified = new Date().toISOString(); // ensure dateModified updates if image_url was in updates
    }
    if (Object.keys(finalUpdateData).length === 0) { // No actual data changed
        const currentRecipe = await supabase.from(RECIPES_TABLE).select().eq('id', id).single();
        if(currentRecipe.data) {
            return {
                ...currentRecipe.data,
                tags: parseTags(currentRecipe.data.tags),
            } as Recipe;
        } else {
            throw new Error("Recipe not found for no-op update.")
        }
    }
  }

  const { data, error } = await supabase
    .from(RECIPES_TABLE)
    .update(finalUpdateData)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    throw error;
  }
  // Return recipe with user context
  return await addUserContextToRecipe(data, user.id);
};

export const deleteRecipe = async (id: string): Promise<void> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("User not authenticated");

  // Check if user can delete this recipe (admin or owner)
  const { data: recipe } = await supabase
    .from(RECIPES_TABLE)
    .select('user_id')
    .eq('id', id)
    .single();

  const { data: profile } = await supabase
    .from('user_profiles')
    .select('role')
    .eq('id', user.id)
    .single();

  const isAdmin = profile?.role === 'admin';
  const isOwner = recipe?.user_id === user.id;

  if (!isAdmin && !isOwner) {
    throw new Error("Access denied: You can only delete your own recipes");
  }

  const { error } = await supabase
    .from(RECIPES_TABLE)
    .delete()
    .eq('id', id);

  if (error) {
    throw error;
  }
};

// Legacy function - kept for backward compatibility, but use toggleUserFavorite instead
export const toggleFavorite = async (id: string, _currentValue: boolean): Promise<void> => {
  await toggleUserFavorite(id);
};

export const toggleShare = async (id: string, currentValue: boolean): Promise<void> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("User not authenticated");

  // Check if user can toggle share for this recipe (admin or owner)
  const { data: recipe } = await supabase
    .from(RECIPES_TABLE)
    .select('user_id')
    .eq('id', id)
    .single();

  const { data: profile } = await supabase
    .from('user_profiles')
    .select('role')
    .eq('id', user.id)
    .single();

  const isAdmin = profile?.role === 'admin';
  const isOwner = recipe?.user_id === user.id;

  if (!isAdmin && !isOwner) {
    throw new Error("Access denied: You can only change share status for your own recipes");
  }

  const { error } = await supabase
    .from(RECIPES_TABLE)
    .update({ share: !currentValue })
    .eq('id', id);

  if (error) {
    throw new Error('Failed to toggle share status');
  }
};

export const getAllTags = async (): Promise<string[]> => {
  // This assumes tags are stored in a way that can be queried distinctly.
  // If tags are in an array column in the recipes table:
  const { data, error } = await supabase
    .from(RECIPES_TABLE)
    .select('tags');

  if (error) {
    return [];
  }

  const tagSet = new Set<string>();
  data.forEach(recipe => {
    const tags = parseTags(recipe.tags);
    tags.forEach(tag => {
      if (tag && tag.trim()) {
        tagSet.add(tag.trim());
      }
    });
  });
  return Array.from(tagSet).sort();
  
  // If tags are in a separate TAGS_TABLE, query that instead.
  // const { data, error } = await supabase.from(TAGS_TABLE).select('name');
  // if (error) { console.error("Error getting tags:", error); return []; }
  // return data.map(t => t.name).sort();
};

/**
* Advanced search function for the assistant - searches ALL recipes efficiently
* @param searchTerm The search term (ingredients, recipe names, categories, etc.)
* @param limit Maximum number of results to return (default 10)
* @returns Promise<Recipe[]> Array of matching recipes with IDs
*/
export const searchRecipesForAssistant = async (
 searchTerm: string,
 limit: number = 10
): Promise<Recipe[]> => {
 if (!searchTerm.trim()) {
   return [];
 }

 const term = searchTerm.toLowerCase().trim();
 
 // Build comprehensive search query that searches title, description, ingredients, category, and tags
 const { data, error } = await supabase
   .from(RECIPES_TABLE)
   .select('*')
   .or(`title.ilike.%${term}%,description.ilike.%${term}%,ingredients.ilike.%${term}%,category.ilike.%${term}%,tags.ilike.%${term}%`)
   .order('dateCreated', { ascending: false })
   .limit(limit);

 if (error) {
   return [];
 }

 return data.map(item => ({
   id: item.id,
   title: item.title || '',
   description: item.description || '',
   category: item.category || undefined,
   tags: parseTags(item.tags),
   ingredients: item.ingredients || '',
   method: item.method || '',
   notes: item.notes || '',
   image_url: item.image_url || 'https://bzvzoqvyxfrgaxsokfva.supabase.co/storage/v1/object/sign/recipeimage/chezalex.jpg?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCIsImtpZCI6InN0b3JhZ2UtdXJsLXNpZ25pbmcta2V5XzgyNzVmMTJjLTQ3YzktNDAzYy05MzgwLTgxYWFjMDliYThiMCJ9.eyJ1cmwiOiJyZWNpcGVpbWFnZS9jaGV6YWxleC5qcGciLCJpYXQiOjE3NDg1NzgyNzAsImV4cCI6MjA2MzkzODI3MH0.TlsA1In_3a7hQIU4W-rrCAqgJu3OOoFiiW-TTT9nOUY',
   has_custom_image: item.has_custom_image || false,
   dateCreated: item.dateCreated,
   dateModified: item.dateModified,
   isFavorite: item.isFavorite || false,
   share: item.share !== undefined ? item.share : true,
 } as Recipe));
};

/**
* Get total recipe count for the assistant (without loading all recipes)
* @returns Promise<number> Total number of recipes in database
*/
export const getRecipeCount = async (): Promise<number> => {
 const { count, error } = await supabase
   .from(RECIPES_TABLE)
   .select('*', { count: 'exact', head: true });

 if (error) {
   return 0;
 }

 return count || 0;
};

// Add functions for image uploads to Supabase Storage if needed
// export const uploadRecipeImage = async (file: File): Promise<string> => {
//   const fileName = `${Date.now()}-${file.name}`;
//   const { data, error } = await supabase.storage
//     .from('recipe-images') // Your bucket name
//     .upload(fileName, file);

//   if (error) {
//     console.error('Error uploading image:', error);
//     throw error;
//   }
//   // Construct the public URL. Note: This might vary based on your bucket settings.
//   const { data: { publicUrl } } = supabase.storage.from('recipe-images').getPublicUrl(fileName);
//   return publicUrl;
// };
